/**
 * Communities.
 *
 * From docs/references/19-communities.png: a New community row at the top,
 * then each community as a block — its header, the announcement group, a
 * few member groups, and View all.
 *
 * The structure is worth stating because it is the whole idea: a community
 * is a container for groups, with one announcement group only admins can
 * post to. Its groups are ordinary group chats that gained a parent, which
 * is why they open in the normal conversation screen and appear in the
 * normal chat list.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { communities as communityService, previewText } from '@zivapp/shared'
import Avatar from '../components/ui/Avatar.jsx'
import './Communities.css'

/** The megaphone that marks the announcement group, as in the reference. */
function Megaphone () {
  return (
    <span className="cm__megaphone" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M4 9.5h3.2L14 5.2v13.6L7.2 14.5H4a1.5 1.5 0 0 1-1.5-1.5v-2a1.5 1.5 0 0 1 1.5-1.5Z"
          stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M17.4 9a4.4 4.4 0 0 1 0 6" stroke="currentColor" strokeWidth="1.5"
          strokeLinecap="round" />
      </svg>
    </span>
  )
}

/** One group inside a community. */
function GroupRow ({ group }) {
  return (
    <Link className="cm__group" to={`/chats/${group.id}`}>
      {group.isAnnouncement
        ? <Megaphone />
        : <Avatar name={group.name} url={group.avatarUrl} size={42} group />}

      <span className="cm__group-main">
        <span className="cm__group-name">
          {group.isAnnouncement ? 'Announcements' : group.name}
        </span>
        <span className="cm__group-preview">
          {group.lastMessage ? previewText(group.lastMessage) : 'No messages yet'}
        </span>
      </span>

      {group.unreadCount > 0 && (
        <span className="cm__badge">
          {group.unreadCount > 999 ? '999+' : group.unreadCount}
        </span>
      )}
    </Link>
  )
}

export default function Communities () {
  const navigate = useNavigate()
  const [list, setList] = useState([])
  const [isLoading, setLoading] = useState(true)

  useEffect(() => communityService.subscribeToMine(next => {
    setList(next)
    setLoading(false)
  }), [])

  return (
    <div className="cm">
      <header className="cm__bar">
        <h1 className="cm__title">Communities</h1>
        <button className="cm__icon-btn" type="button" aria-label="Menu">
          <svg viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="5" r="1.7" fill="currentColor" />
            <circle cx="12" cy="12" r="1.7" fill="currentColor" />
            <circle cx="12" cy="19" r="1.7" fill="currentColor" />
          </svg>
        </button>
      </header>

      <div className="cm__scroll">
        <button
          type="button"
          className="cm__new"
          data-testid="new-community"
          onClick={() => navigate('/communities/new')}
        >
          <span className="cm__new-badge">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="9" cy="9" r="3.1" stroke="currentColor" strokeWidth="1.8" />
              <path d="M3.6 18.4v-1a3.6 3.6 0 0 1 3.6-3.6h3.6a3.6 3.6 0 0 1 3.6 3.6v1"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M16.6 5.4a3 3 0 0 1 0 5.6" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <span className="cm__new-plus" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="3.4"
                  strokeLinecap="round" />
              </svg>
            </span>
          </span>
          New community
        </button>

        {list.map(community => {
          // The announcement group is pulled out of the list and shown
          // first, as in the reference — it is the one group everybody in
          // the community is in, so it is never a row among equals.
          const rest = community.groups
          const shown = community.announcement
            ? [community.announcement, ...rest]
            : rest

          return (
            <section className="cm__block" key={community.id}>
              <Link className="cm__head" to={`/communities/${community.id}`}>
                <Avatar name={community.name} url={community.avatarUrl} size={48} group />
                <span className="cm__head-text">
                  <span className="cm__head-name">{community.name}</span>
                  {community.isAdmin && <span className="cm__head-role">Admin</span>}
                </span>
              </Link>

              {shown.map(group => <GroupRow key={group.id} group={group} />)}

              <Link className="cm__viewall" to={`/communities/${community.id}`}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="1.9"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                View all
              </Link>
            </section>
          )
        })}

        {!isLoading && list.length === 0 && (
          <div className="cm__empty">
            <span className="cm__empty-art" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="8.5" cy="8.6" r="3" stroke="currentColor" strokeWidth="1.4" />
                <path d="M3.4 19v-1.4a3.4 3.4 0 0 1 3.4-3.4h3.4a3.4 3.4 0 0 1 3.4 3.4V19"
                  stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                <path d="M16 6.2a3 3 0 0 1 0 5.6M17 14.2c2 .4 3.4 2 3.4 4V19"
                  stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </span>

            <p className="cm__empty-title">No communities yet</p>
            <p className="cm__empty-text">
              A community brings related groups together under one roof, with an
              announcement group only admins can post to.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
